import type { DatabookSheetPreview } from "@/lib/api/databook";

export interface DatabookCellPosition {
  row: number;
  column: number;
}

export interface DatabookCellSelection {
  sheetName: string;
  anchor: DatabookCellPosition;
  focus: DatabookCellPosition;
}

export interface DatabookCellSelectionBounds {
  minRow: number;
  maxRow: number;
  minColumn: number;
  maxColumn: number;
}

export function startDatabookCellSelection(
  sheetName: string,
  current: DatabookCellSelection | null,
  position: DatabookCellPosition,
  extend: boolean,
): DatabookCellSelection {
  if (extend && current?.sheetName === sheetName) {
    return { ...current, focus: position };
  }
  return {
    sheetName,
    anchor: position,
    focus: position,
  };
}

export function extendDatabookCellSelection(
  current: DatabookCellSelection,
  focus: DatabookCellPosition,
): DatabookCellSelection {
  return { ...current, focus };
}

export function databookCellSelectionBounds(
  selection: DatabookCellSelection | null,
  sheetName: string,
): DatabookCellSelectionBounds | null {
  if (!selection || selection.sheetName !== sheetName) return null;
  return {
    minRow: Math.min(selection.anchor.row, selection.focus.row),
    maxRow: Math.max(selection.anchor.row, selection.focus.row),
    minColumn: Math.min(selection.anchor.column, selection.focus.column),
    maxColumn: Math.max(selection.anchor.column, selection.focus.column),
  };
}

export function isCellInDatabookSelection(
  bounds: DatabookCellSelectionBounds | null,
  row: number,
  column: number,
): boolean {
  return Boolean(
    bounds
      && row >= bounds.minRow
      && row <= bounds.maxRow
      && column >= bounds.minColumn
      && column <= bounds.maxColumn,
  );
}

export function databookCellSelectionA1(
  selection: DatabookCellSelection | null,
  sheet: DatabookSheetPreview | null,
): string | null {
  if (!sheet) return null;
  const bounds = databookCellSelectionBounds(selection, sheet.name);
  if (!bounds) return null;
  const rowSourceIndexes = new Map(
    sheet.rows.map((row) => [row.index, row.source_index]),
  );
  const columnSourceIndexes = new Map(
    sheet.columns.map((column) => [column.index, column.source_index]),
  );
  const startRow = projectedSourceIndex(
    rowSourceIndexes,
    sheet.rows,
    bounds.minRow,
  );
  const endRow = projectedSourceIndex(
    rowSourceIndexes,
    sheet.rows,
    bounds.maxRow,
  );
  const startColumn = projectedSourceIndex(
    columnSourceIndexes,
    sheet.columns,
    bounds.minColumn,
  );
  const endColumn = projectedSourceIndex(
    columnSourceIndexes,
    sheet.columns,
    bounds.maxColumn,
  );
  if (
    startRow === undefined
    || endRow === undefined
    || startColumn === undefined
    || endColumn === undefined
  ) {
    return null;
  }
  const start = `$${excelColumnName(startColumn)}$${startRow + 1}`;
  const end = `$${excelColumnName(endColumn)}$${endRow + 1}`;
  return start === end ? start : `${start}:${end}`;
}

function projectedSourceIndex(
  sourceIndexes: Map<number, number>,
  entries: Array<{ index: number; source_index: number }>,
  projectedIndex: number,
): number | undefined {
  const sourceIndex = sourceIndexes.get(projectedIndex);
  if (sourceIndex !== undefined) return sourceIndex;
  const lastEntry = entries.at(-1);
  if (!lastEntry || projectedIndex <= lastEntry.index) return undefined;
  return lastEntry.source_index + projectedIndex - lastEntry.index;
}

export function excelColumnName(columnIndex: number): string {
  let value = columnIndex + 1;
  let result = "";
  while (value > 0) {
    const remainder = (value - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    value = Math.floor((value - 1) / 26);
  }
  return result;
}
